import core from '@gin-betting/core';
import { includes, noop, transform, uniq, values } from 'lodash';

import {
    ANALYTIC_LOCALSTORAGE_KEY,
    BettingSettingsKeys,
    ExtendedRouts,
    FILTER_LOCATION_SEARCH,
    PublicRoutes,
    SlugPublicRoutes,
} from 'app-constants';
import type { IBettingAPI, SearchObj } from 'types';
import { parse, stringify } from 'utils';
import { FieldsObj } from './types';

const SLUG = 'slug' as const;

const {
    constants: { ROUTE_DIVIDER },
    utils: { getBettingOptions },
} = core;

const routes = uniq([
    ...values(ExtendedRouts),
    ...SlugPublicRoutes,
    ...PublicRoutes,
]);

const LOG_TRACKER: UniversalAnalytics.Tracker = {
    get: noop,
    set: noop,
    send: console.log,
};

let isEnable = false;
let isSubscribed = false;

const getTracker = (): UniversalAnalytics.Tracker | undefined => {
    const bettingApi: IBettingAPI | undefined = (window as any).bettingAPI;

    if (bettingApi && !isSubscribed) {
        isSubscribed = true;

        bettingApi.subscribeOnBettingSetting(
            BettingSettingsKeys.IS_ENABLE_ANALYTICS,
            (flag: boolean) => {
                isEnable = flag;
            }
        );
    }

    if (!isEnable) return;

    const isAnalyticTurnedOn = !!localStorage.getItem(
        ANALYTIC_LOCALSTORAGE_KEY
    );

    if (isAnalyticTurnedOn) {
        return LOG_TRACKER;
    }

    // NOTE: If you happen to be using multiple, you may need to fetch the appropriate one by name or index.
    const GAIndex = 0 as const;

    const getAll = (window.ga as UniversalAnalytics.ga | undefined)?.getAll;

    if (typeof getAll !== 'function') return;

    // window.ga can be undefined
    return getAll()[GAIndex];
};

let ffCache: string | undefined;

const getParticipantFeaturesLabel = (): string => {
    if (typeof ffCache !== 'undefined') return ffCache;

    const { participantFeatures } = getBettingOptions();

    const formattedFeaturesData = Object.values(participantFeatures).reduce(
        (acc, { scenario_identifier, feature_variant, for_analysis }) => {
            return for_analysis
                ? [
                      ...acc,
                      {
                          name: scenario_identifier,
                          variant: feature_variant,
                      },
                  ]
                : acc;
        },
        []
    );

    const formattedFeaturesString = formattedFeaturesData.length
        ? `FF: ${JSON.stringify(formattedFeaturesData)}`
        : '';

    ffCache = formattedFeaturesString;

    return formattedFeaturesString;
};

export const sendAnalytics = (
    fieldsObj: FieldsObj,
    label: string | undefined | null
): void => {
    const tracker = getTracker();

    if (!tracker) return;

    const labels = [label, getParticipantFeaturesLabel()].filter(l => !!l);

    if (labels.length) {
        fieldsObj.eventLabel = labels.join('; ');
    }

    tracker.send('event', fieldsObj);
};

export const formatPathname = (pathname: string): string => {
    const isRouteWithSlug = routes.every(route => route !== pathname);

    return isRouteWithSlug
        ? pathname
              .split(ROUTE_DIVIDER)
              .slice(0, -1)
              .concat(SLUG)
              .join(ROUTE_DIVIDER)
        : pathname;
};

export const formatSearch = (search: string): string => {
    const object = parse(search);
    const searchTransform = transform(
        object,
        (result, value, key) => {
            result[key] = includes(FILTER_LOCATION_SEARCH, key)
                ? ''
                : (value as any);
        },
        {} as SearchObj
    );

    const s = stringify(searchTransform);

    return s ? `?${s}` : s;
};
